import React from 'react';

import { CardItemRender } from './types';

interface Params {
  description?: string | CardItemRender;
  collapsedHeight?: number;
}

interface UseDescriptionExpandableReturn {
  ref: React.RefObject<HTMLDivElement>;
  descriptionState: {
    expanded: boolean;
    expandable: boolean;
  };
  expand(): void;
  collapse(): void;
  toggle(): void;
}

const useDescriptionExpandable = ({
  description,
  collapsedHeight = 26,
}: Params): UseDescriptionExpandableReturn => {
  const descriptionRef = React.useRef<HTMLDivElement>(null);
  const [descriptionExpanded, setDescriptionExpanded] = React.useState(false);
  const [descriptionExpandable, setDescriptionExpandable] = React.useState(
    false,
  );

  const checkDescriptionExpandable = React.useCallback(() => {
    const element = descriptionRef.current;

    if (!element || !description) {
      setDescriptionExpandable(false);
      return;
    }

    const paragraph = element.querySelector('p');
    const hasParagraphOverflow = paragraph
      ? paragraph.scrollHeight > paragraph.clientHeight ||
        paragraph.scrollWidth > paragraph.clientWidth
      : false;
    const overflowsCollapsedHeight = element.scrollHeight > collapsedHeight;

    setDescriptionExpandable(hasParagraphOverflow || overflowsCollapsedHeight);
  }, [collapsedHeight, description]);

  React.useLayoutEffect(() => {
    checkDescriptionExpandable();
  }, [checkDescriptionExpandable, descriptionExpanded]);

  React.useEffect(() => {
    if (!descriptionRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      checkDescriptionExpandable();
    });

    observer.observe(descriptionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [checkDescriptionExpandable]);

  React.useEffect(() => {
    if ((!description || !descriptionExpandable) && descriptionExpanded) {
      setDescriptionExpanded(false);
    }
  }, [description, descriptionExpandable, descriptionExpanded]);

  return {
    ref: descriptionRef,
    descriptionState: {
      expanded: descriptionExpanded,
      expandable: descriptionExpandable,
    },
    expand: () => setDescriptionExpanded(true),
    collapse: () => setDescriptionExpanded(false),
    toggle: () => setDescriptionExpanded(prev => !prev),
  };
};

export default useDescriptionExpandable;
